// HexSilhouettes renders the outline of every LAND tile as a single
// LineSegments mesh, so the land reads as discrete cells. The mesh is
// built by `buildHexSilhouetteGeometry(tiles, biomeMap)`, which emits one
// line segment per tile edge (2 verts) at the tile rim, each outline at its
// own tile's surface height (tileSurfaceY). A single fixed height made the
// lines float above low tiles and sink into high ones, so in perspective
// they drifted off the real tile edges.
//
// Water tiles get no outline, and neither does a land edge that faces water:
// the dark grid over the sea (and a dark line along every shore) was the hex
// mosaic the coastline fix removes. The sea is one continuous surface.

import type { SceneNode } from '@aod/protocol';
import { useMemo } from 'react';
import * as THREE from 'three';
import { RenderLayer } from './RenderLayer.js';
import { TILE_RADIUS, tileSurfaceY } from './elevation.js';
import { shoreSignature } from './shore/shoreBoard.js';
import { EDGE_NEIGHBOURS, WATER_BIOMES } from './terrainEdges.js';

/** Lift above the tile surface to avoid z-fighting with the tile mesh. */
export const OUTLINE_LIFT = 0.006;

/** Stroke colour for the hex outlines — dark, slightly translucent. */
const STROKE_COLOR = '#0c0d18';
const STROKE_OPACITY = 0.55;

/** Outlines trace the tile rim (TILE_RADIUS), at the bottom of the chamfer. */
const HEX_RADIUS = TILE_RADIUS;
const RIM_DROP = 0.03;

/**
 * Six corner offsets at the hex shape, pointy-up, in the XZ plane.
 * Vertex 0 sits at angle 30° from +X (upper-right corner); the rest fan
 * around at +60° increments.
 */
const HEX_CORNER_OFFSETS: readonly [
  readonly [number, number, number],
  readonly [number, number, number],
  readonly [number, number, number],
  readonly [number, number, number],
  readonly [number, number, number],
  readonly [number, number, number],
] = (() => {
  const out: Array<readonly [number, number, number]> = [];
  for (let i = 0; i < 6; i++) {
    const angle = ((30 + 60 * i) * Math.PI) / 180;
    out.push([Math.cos(angle) * HEX_RADIUS, 0, Math.sin(angle) * HEX_RADIUS]);
  }
  return out as unknown as typeof HEX_CORNER_OFFSETS;
})();

/**
 * Build a non-indexed BufferGeometry with 2 verts per land edge (12 for a tile
 * with no water neighbour), positioned at the tile's world transform, on the tile's surface.
 *
 * Non-indexed so each edge is its own line segment — an indexed geometry
 * would dedupe shared vertices and turn the hex into a triangle fan with
 * invisible diagonals visible as faint artifacts.
 */
export function buildHexSilhouetteGeometry(
  tiles: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  if (tiles.length === 0) {
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(0), 3));
    return geo;
  }

  const verts = new Float32Array(tiles.length * 12 * 3);
  let v = 0;
  for (const tile of tiles) {
    const [tx, , tz] = tile.transform.position;
    const props = tile.props as Record<string, unknown> | undefined;
    const q = (props?.q as number) ?? 0;
    const r = (props?.r as number) ?? 0;
    if (WATER_BIOMES.has((props?.biome as string | undefined) ?? 'plains')) continue;
    const y = tileSurfaceY(q, r, biomeMap) - RIM_DROP + OUTLINE_LIFT;
    for (let e = 0; e < 6; e++) {
      const [dq, dr] = EDGE_NEIGHBOURS[e]!;
      const nb = biomeMap.get(`${q + dq},${r + dr}`);
      if (nb !== undefined && WATER_BIOMES.has(nb)) continue;
      const [ax, ay, az] = HEX_CORNER_OFFSETS[e]!;
      const [bx, by, bz] = HEX_CORNER_OFFSETS[(e + 1) % 6]!;
      verts[v++] = tx + ax;
      verts[v++] = y + ay;
      verts[v++] = tz + az;
      verts[v++] = tx + bx;
      verts[v++] = y + by;
      verts[v++] = tz + bz;
    }
  }

  geo.setAttribute('position', new THREE.BufferAttribute(verts.slice(0, v), 3));
  return geo;
}

/**
 * Render a single LineSegments mesh wrapping all the tile outlines. The
 * mesh is drawn just above the tile top surface so the stroke reads
 * without z-fighting.
 */
export function HexSilhouettes({
  tiles,
  biomeMap,
}: {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly biomeMap: Map<string, string>;
}) {
  // Keyed on the terrain signature, not the per-publish tile array (renderer-crash-rca #139/#140).
  const signature = useMemo(() => shoreSignature(tiles), [tiles]);
  const geometry = useMemo(() => buildHexSilhouetteGeometry(tiles, biomeMap), [signature]);
  const material = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: STROKE_COLOR,
        transparent: true,
        opacity: STROKE_OPACITY,
        depthWrite: false,
      }),
    [],
  );
  if (tiles.length === 0) return null;
  return (
    <lineSegments
      name="hex-outlines"
      geometry={geometry}
      material={material}
      renderOrder={RenderLayer.Terrain + 0.1}
    />
  );
}
